import React from 'react';
import type { SxProps } from '@mui/system';
import { useTranslation } from 'next-i18next';

import Box from '@mui/material/Box';

import Avatar from 'src/components/ui/Avatar';

import type { Medium, User } from 'src/types/__generated__/graphql';
import { styledComponent, styledTag } from 'src/utils/styled';
import Icon from 'src/components/ui/Icon';
import { colours } from 'src/constants/colours';
import Tooltip from 'src/components/ui/Tooltip';

const avatarContainer: SxProps = {
    '> *': {
        '&:first-of-type': {
            top: 0,
            left: 0,
        },
        '&:nth-of-type(2)': {
            bottom: 0,
            right: 0,
        },
    },
};

const StyledAvatar = styledComponent(Avatar)<{ $overlapMargin: string }>`
    margin-left: ${({ $overlapMargin }) => `-${$overlapMargin}`};
    border: 0.063rem solid;
`;

const PlusIconButton = styledTag('div')<{ $overlapMargin: string }>`
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1;
    margin-left: ${({ $overlapMargin }) => `-${$overlapMargin}`};
    background-color: ${colours.backgroundGray};
    border-radius: 50%;
    height: 1.9rem;
    width: 1.9rem;
    border: 0.063rem solid ${colours.white.main};
    cursor: pointer;
`;

const StyledTooltip = styledComponent(Tooltip)`
    z-index: 1;
`;

export type UserData = {
    id: User['id'];
    fullName: User['fullName'];
    initials: User['initials'];
    image: Pick<Medium, 'url'> | null;
};

interface Props {
    users: UserData[];
    additionalUsersCount?: number;
    width?: string;
    avatarSize?: number;
    overlapMargin?: string;
    showAddUserButton?: boolean;
    onAddUserClick?: () => void;
}

export const AvatarList: React.FC<Props> = ({
    users,
    additionalUsersCount,
    width,
    avatarSize,
    overlapMargin = '1rem',
    showAddUserButton,
    onAddUserClick,
}) => {
    const { t } = useTranslation(['favorites']);
    return (
        <Box sx={avatarContainer} display="flex" width={width} position="relative">
            {users.map((user) => (
                <StyledAvatar
                    key={user.id}
                    image={user.image as Medium}
                    alt={user.fullName}
                    fallbackText={user.initials}
                    size={avatarSize}
                    $overlapMargin={overlapMargin}
                />
            ))}
            {Boolean(additionalUsersCount && additionalUsersCount > 0) && (
                <StyledAvatar
                    fallbackText={`+${additionalUsersCount}`}
                    size={avatarSize}
                    $overlapMargin={overlapMargin}
                />
            )}
            {showAddUserButton && (
                <PlusIconButton
                    $overlapMargin={overlapMargin}
                    onClick={onAddUserClick}
                    className="add-user-icon-button"
                >
                    <Icon name="AddUserWhite" size="1.1rem" color={colours.white.main} />
                </PlusIconButton>
            )}
        </Box>
    );
};
